import { AirlineApplicableDeviationRequests, TravelRequestData, TravelRequestDetail, TravelRequestDetailApi, TravelRequestHistoryRecord, TravelRequestPermissions, TravelRequestResponse } from 'api/travel-request-detail';
import { autoinject, computedFrom, newInstance } from 'aurelia-framework';
import { Redirect, RouteConfig, Router } from 'aurelia-router';
import { ValidationController } from 'aurelia-validation';
import { watch } from 'aurelia-watch-decorator';
import { TravelRequestFormCustomElement } from 'components/travel-request-form';
import { DialogHelper } from 'framework/aurelia_dialogs/dialog-helper';
import { BootstrapGroupRenderer } from 'framework/aurelia_tools/bootstrap-group-renderer';
import { EventHelper, Subscription } from 'framework/aurelia_tools/event-helper';
import { log } from 'framework/script_utilities/dev-decorators';
import { cloneDeep } from 'framework/script_utilities/object-equalizer';
import { AirlineStatus, ApprovalStatus, BusinessFormType } from 'model/business-enums';
import { BusinessTraveler, CountryVisit, initBusinessContact, initBusinessDetails, initCorporateTraveler, initCountryExperience, initTravelRequest, produceCountryRequest, TravelRequest } from 'model/forms/travel-request';
import { extractTripCountries, initCitySegment, TransportSegment } from 'model/forms/trip-details';
import { EnumsService } from 'services/enums-service';
import { IdentityService } from 'services/identity-service';
import { KeywordsService } from 'services/keywords-service';
import { LookupsService } from 'services/lookups-service';
import { aprovalRequired, SafetyService } from 'services/safety-service';
import { requireServices } from 'support/activation';
import { DataTransferer } from 'support/shared_models/data-transferer';
import { FormSettings } from 'support/shared_models/form-settings';
import { ToastFormat, ToastHelper, ToastType } from 'support/toaster';
import { ValidationHelper } from 'support/validation';
import { ResponseSubmitted } from 'widgets/country-approver';
import { FilesUploadController } from 'framework/script_utilities/files-upload-controller';
import { MobileAppService } from 'services/mobile-app-service';
import { ApplicableDeviationsDialog, ApplicableDeviationsOptions } from './applicable-deviations-dialog';
import { RequestAttachmentsController } from 'components/request-attachments-panel';
import { FileParameter, UploadedFile } from 'model/essential-types';
import { downloadFile } from 'framework/script_utilities/file-utils';
import { fileSizeToView } from 'framework/script_utilities/text-utils';
import { ErrorInfo } from 'framework/error_handling/error-handler';
import { TravelRequestsService } from 'services/travel-requests-service';

@autoinject
export class TravelRequestDetailScreen {
  id: number = null;
  route: RouteConfig = null;
  initialCountries: CountryVisit[] = null;
  form: TravelRequestFormCustomElement = null;
  model: TravelRequest | TravelRequestData = null;
  airlinesApplicableDeviations: AirlineApplicableDeviationRequests[] = [];
  permissions: TravelRequestPermissions = null;
  history: TravelRequestHistoryRecord[] = null;
  backup: TravelRequestDetail = null;
  loaded = false;
  editing = false;
  changed = false;
  isAnyApprovableCountry = false;
  private responseSubmittedSubscription: Subscription = null;
  attachmentsController: RequestAttachmentsController = null;

  constructor(private identity: IdentityService, private keywords: KeywordsService, private enums: EnumsService, private lookups: LookupsService, private safety: SafetyService, private api: TravelRequestDetailApi, private router: Router, private transferer: DataTransferer, private dialogHelper: DialogHelper, private toastHelper: ToastHelper, private validationHelper: ValidationHelper, private eventHelper: EventHelper, @newInstance() private formSettings: FormSettings, @newInstance() private validationController: ValidationController, validationRenderer: BootstrapGroupRenderer, private mobileAppService: MobileAppService, @newInstance() private filesUploadController: FilesUploadController, private travelRequestsService: TravelRequestsService) {
    formSettings.formType = BusinessFormType.TravelRequest;
    validationRenderer.allowedParents = 'bs-form-input, bs-form-lookup, bs-form-select, bs-form-textarea';
    this.validationController.addRenderer(validationRenderer);
    this.attachmentsController = {
      attachments: [],
      maxAllowedAttachmensCount: this.travelRequestsService.maxAllowedAttachmensCount,
      allowedFileExtensions: this.travelRequestsService.allowedFileExtensions,
      filesUploadController: this.filesUploadController,
      maximumAllowedAttachmentsExceeded: () => {
        this.toastHelper.error({ title: 'File upload failed', message: `Cannot upload a new attachment as there can be maximum ${this.travelRequestsService.maxAllowedAttachmensCount} attachments per Travel Request.` } as ErrorInfo);
      },
      upload: (attachment: FileParameter) => {
        const fileExtension = `.${attachment.fileName.split('.').pop()}`;
        if (attachment.data.size > this.travelRequestsService.maxFileSizeInBytes) {
          this.toastHelper.error({ title: 'File upload failed', message: `Selected file ${attachment.fileName} is too big. Please upload files with maximum size of ${fileSizeToView(this.travelRequestsService.maxFileSizeInBytes)}.` } as ErrorInfo);
          return Promise.reject();
        }
        else if (!this.travelRequestsService.allowedFileExtensions.some(x => x === fileExtension)) {
          this.toastHelper.error({ title: 'File upload failed', message: `Type of selected file ${attachment.fileName} is not supported. Please select supported file type.` } as ErrorInfo);
          return Promise.reject();
        }
        else {
          return this.api.uploadAttachment(this.id, attachment)
            .then(x => {
              this.model.attachments.push(x);
              return x;
            })
            .catch((reason) => {
              this.toastHelper.error({ title: 'File upload failed', message: `Uploading file ${attachment.fileName} has failed. Please try to upload the file again.`, details: reason } as ErrorInfo);
              return Promise.reject(reason);
            });
        }
      },
      download: (attachment: UploadedFile) => {
        return this.api.downloadAttachment(this.id, attachment).then(x => {
          const url = URL.createObjectURL(x.data);
          try { downloadFile(url, `${attachment.fileName}${attachment.extension}`); }
          finally { URL.revokeObjectURL(url); }
        });
      },
      remove: (attachment: UploadedFile) => {
        this.model.attachments.splice(this.model.attachments.indexOf(attachment), 1);
      }
    } as RequestAttachmentsController;
  }

  async canActivate() {
    await requireServices(this.identity, this.enums, this.keywords, this.lookups, this.safety);
  }

  @log('debug')
  async activate(params, route: RouteConfig) {
    this.id = parseInt(params?.id) || null;
    if (this.id == null) {
      if (params?.countryIds != null) {
        const transits = (params?.transits as string[]).map(x => x === 'true');
        this.initialCountries = (params.countryIds as number[]).map((value, index) => ({ country: this.keywords.countries.find(x => x.id == value), transit: transits[index] } as CountryVisit));
      }
      if (params.countryIsoCode2 != null) {
        const country = this.keywords.countries.find(x => x.isoCode2 == params.countryIsoCode2);
        if (country != null)
          this.initialCountries = [{ country: country, transit: false } as CountryVisit];
      }
    }
    this.route = route;
  }

  bind() {
    this.initialize();
  }

  private async initialize() {
    this.bindDetail(this.id ? await this.api.getTravelRequest(this.id) : null);
    this.editing = this.id && this.permissions.canEdit && this.autoEdit || !this.id;
    this.loaded = true;
  }

  attached() {
    this.responseSubmittedSubscription = this.eventHelper.subscribe(ResponseSubmitted, e => e.handlerPromise = this.submitResponse(e));
  }

  detached() {
    this.responseSubmittedSubscription.dispose();
  }

  async canDeactivate() {
    if (this.editing && this.changed && !await this.dialogHelper.confirm({ dialogTitle: 'Leave without saving?', message: 'You haven\'t saved the request yet. If you proceed, your changes will be lost!', okTitle: 'Leave without saving', cancelTitle: 'Stay here!' }))
      return new Redirect((this.router.history as any).previousLocation, { trigger: false, replace: false });
  }

  @log('debug')
  private bindDetail(detail?: TravelRequestDetail) {
    this.id = detail?.data.id ?? null;
    this.model = detail?.data ?? this.getEmptyForm();
    this.permissions = detail?.permissions ?? null;
    this.airlinesApplicableDeviations = detail?.airlinesApplicableDeviations ?? [];
    this.attachmentsController.attachments = this.model.attachments;
    this.history = detail?.history ?? null;
    this.backup = cloneDeep(detail);
  }

  private get autoEdit() {
    return false;
  }

  applicableDeviationsButtonVisible = false;
  editButtonClicked = false;
  editButtonVisible = false;
  archiveButtonVisible = false;
  restoreButtonVisible = false;
  submitButtonVisible = false;
  cancelButtonVisible = false;

  @watch((x: TravelRequestDetailScreen) => x.model)
  @watch((x: TravelRequestDetailScreen) => x.permissions)
  @watch((x: TravelRequestDetailScreen) => x.editing)
  @watch((x: TravelRequestDetailScreen) => x.changed)
  @watch((x: TravelRequestDetailScreen) => x.isAnyApprovableCountry)
  @watch((x: TravelRequestDetailScreen) => x.editButtonClicked)
  private updateButtons() {
    this.applicableDeviationsButtonVisible = !this.editing && this.getAirlinesTransportSegmentsToWarn().length > 0;
    this.editButtonVisible = !this.editing && this.permissions?.canEdit;
    this.archiveButtonVisible = !(this.editing && this.changed) && this.permissions?.canArchive && (this.model as TravelRequestData)?.archivedOn == null;
    this.restoreButtonVisible = !(this.editing && this.changed) && this.permissions?.canRestore && (this.model as TravelRequestData)?.archivedOn != null;
    this.submitButtonVisible = this.editing && this.changed && this.isAnyApprovableCountry;
    this.cancelButtonVisible = this.editing && (this.changed || this.editButtonClicked);
  }

  @watch((x: TravelRequestDetailScreen) => x.editing)
  private updateForm(editing: boolean) {
    if (this.id && editing) this.form.update();
  }

  async applicableDeviations() {
    await this.dialogHelper.open(ApplicableDeviationsDialog, {
      travelRequest: this.model,
      airlinesTransportSegments: this.getAirlinesTransportSegmentsToWarn(),
      airlinesApplicableDeviations: this.airlinesApplicableDeviations
    } as ApplicableDeviationsOptions);
  }

  async edit() {
    if ((this.model as TravelRequestData).status?.value === ApprovalStatus.RequireMoreInfo || await this.dialogHelper.confirm({ dialogTitle: 'Do you need to make changes?', message: 'A change in any part of the request will restart the approval process. Do you want to continue?', okTitle: 'Yes, I need to make changes', cancelTitle: 'Cancel' }))
      this.editing = this.editButtonClicked = true;
  }

  async submit() {
    const airlinesWarnings = this.getAirlinesWarnings();
    await this.validationHelper.validate(this.validationController)
      && (airlinesWarnings.length === 0 || await this.dialogHelper.confirm({ icon: 'fas fa-clipboard-check', dialogTitle: 'Deviations Warning', message: `You have selected following non approved or approved by deviation airlines:<ul><li>${airlinesWarnings.join("</li><li>")}</li></ul>After submitting the request please check there are appropriate Deviations or submit new ones.`, okTitle: 'Ok, let\'s submit!', cancelTitle: 'Back to form' }))
      && await this.filesUploadController.waitForUpload()
      && await this.save();
  }

  async cancel() {
    if (!this.changed || await this.dialogHelper.confirm({ dialogTitle: 'Cancel your changes?', message: 'You haven\'t saved the request yet. If you proceed, your changes will be lost!', okTitle: 'Sure, do it!', cancelTitle: 'Back to form' })) {
      this.bindDetail(this.backup);
      this.editing = !this.editButtonClicked;
    }
  }

  async archive() {
    this.bindDetail(await this.api.archiveTravelRequest(this.id));
    this.toastHelper.toast('Your travel request has been successfully <b>archived</b>.', 'Archived', ToastType.Success, ToastFormat.Html);
    this.editing = this.editing && this.permissions.canEdit;
  }

  async restore() {
    this.bindDetail(await this.api.restoreTravelRequest(this.id));
    this.toastHelper.toast('Your travel request has been successfully <b>restored</b>.', 'Restored', ToastType.Success, ToastFormat.Html);
    this.editing = this.permissions.canEdit && this.autoEdit;
  }

  private async save() {
    const isNew = this.id == null;
    const data = this.getSaveData(this.model);
    this.bindDetail(this.id ? await this.api.updateTravelRequest(this.id, data) : await this.api.createTravelRequest(data));
    this.toastHelper.toast('Your travel request has been successfully <b>saved</b>.', 'Saved', ToastType.Success, ToastFormat.Html);
    this.editButtonClicked ? this.editing = false : (this.mobileAppService.isMobileApp && isNew ? this.router.navigateToRoute(this.router.currentInstruction.config.name, { mobileAppResult: 'success' }, { trigger: false, replace: true }) : this.router.navigateBack());
  }

  private async submitResponse(e: ResponseSubmitted) {
    const response: TravelRequestResponse = { countryId: e.country.id, approverId: e.approver.approver.id, statusValue: e.statusValue, comment: e.comment };
    this.bindDetail(await this.api.submitApprovalResponse(this.id, response));
    this.toastHelper.toast('Your response has been successfully <b>submitted</b>.', 'Submitted', ToastType.Success, ToastFormat.Html);
  }

  getSaveData(model: TravelRequest): TravelRequest {
    const data = cloneDeep(model);
    data.businessDetails.businessContacts = data.businessDetails.businessContacts.filter(r => r.name != null || r.phone != null || r.mail != null);
    return data;
  }

  getEmptyForm(): TravelRequest {
    const segments = this.initialCountries != null && this.initialCountries.length > 0 ? this.initialCountries.map(v => initCitySegment({ country: v.country })) : [initCitySegment()];
    const countryData = extractTripCountries(segments).map(c => ({ country: c, settings: this.safety.countrySettings.get(c.id), transit: this.initialCountries.find(v => v.country.id === c.id)?.transit })).filter(x => x.settings != null);
    return initTravelRequest({
      segments: segments,
      travelers: [initCorporateTraveler({ countryExperiences: countryData.filter(x => aprovalRequired(x.settings.approvalRequirementValue, x.transit)).map(x => initCountryExperience({ country: x.country })) })],
      businessDetails: initBusinessDetails({ businessContacts: [initBusinessContact()] }),
      countryRequests: countryData.map(x => produceCountryRequest(x.country, x.settings, x.transit)),
      checklistItems: this.safety.getApplicableQuestions(countryData.map(x => x.country.id)).map(q => { return { message: q.message, checked: q.defaultChecked, mandatory: q.mandatory }; })
    });
  }

  @computedFrom('id', 'model')
  get title() {
    const getTravelerName = (t: BusinessTraveler) => t.kind === 'corporate-traveler' ? (t.user == null ? null : t.user.name.split(' ')[0]) : (t.firstName == null ? null : t.firstName.trim());
    return [this.id == null ? 'New' : this.model?.travelers.map(t => getTravelerName(t)).filter(n => n != null).map(n => `${n}'s`).join(' & '), 'Travel Request'].filter(p => p).join(' ');
  }

  @computedFrom('model.status')
  get statusTooltip() {
    switch ((this.model as TravelRequestData)?.status?.value) {
      case ApprovalStatus.Pending: return 'Your travel request has been submitted and is awaiting approval';
      case ApprovalStatus.Approved: return 'Travel request approved';
      case ApprovalStatus.Declined: return 'Travel request declined';
      case ApprovalStatus.RequireMoreInfo: return 'Additional information is required to approve the trip, see details below';
      default: return null;
    }
  }

  @computedFrom('model.status')
  get statusClass() {
    return (this.model as TravelRequestData)?.status != null ? `--${ApprovalStatus[(this.model as TravelRequestData).status.value].toLowerCase()}` : null;
  }

  @computedFrom('model.status')
  get requireMoreInfo() {
    return (this.model as TravelRequestData)?.status?.value === ApprovalStatus.RequireMoreInfo;
  }

  @computedFrom('applicableDeviationsButtonVisible', 'editButtonVisible', 'archiveButtonVisible', 'restoreButtonVisible', 'submitButtonVisible', 'cancelButtonVisible')
  get headerButtonsVisible() {
    return this.applicableDeviationsButtonVisible || this.editButtonVisible || this.archiveButtonVisible || this.restoreButtonVisible || this.submitButtonVisible || this.cancelButtonVisible;
  }

  @watch((x: TravelRequestDetailScreen) => x.model)
  private modelChanged() {
    this.route.navModel.setTitle(this.title);
    this.validationController.reset();
  }

  private getAirlinesWarnings(): string[] {
    return this.getAirlinesTransportSegmentsToWarn().map(x => x.airline !== null ? `${x.airline.name} (${x.airline.status.name})` : `${x.airlineName} (Non Approved)`);
  }

  private getAirlinesTransportSegmentsToWarn(): TransportSegment[] {
    return this.model.segments.filter(x => {
      if (x.kind === 'transport-segment') {
        const transportSegment = x as TransportSegment;
        return (transportSegment.airline !== null && (transportSegment.airline.status.value === AirlineStatus.ApprovedDeviation || transportSegment.airline.status.value === AirlineStatus.NonApproved)) || (transportSegment.airline === null && transportSegment.airlineName !== null);
      }
      return false;
    }).map(x => x as TransportSegment).sort((a, b) => ('' + a.airlineName).localeCompare(b.airlineName));
  }
}
